import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import Select from 'components/Select/Select';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  BUSINESS_UNIT_TYPE,
  OWNERSHIP,
  PRIORITY_RATING,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';

import BasicCustomerInfo from './BasicCustomerInfo';
import FinancialAndDatesInfo from './FinancialAndDatesInfo';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IFieldError from 'store/types/IFieldError';
import type { IGeneralCustomerInfo } from '../interfaces/IGeneralCustomerInfo';

import styles from '../sass/CreateCustomerView.module.scss';

function GeneralCustomerInfo({
  control,
  enumData,
  placeholder,
  recordId,
  isUniqueIdentifierEditable,
  dateOpened,
  onChangeDate,
  vcOptions,
  handleGetVcOptions,
  vcOptionsList,
  listPosition,
  setValue,
}: IGeneralCustomerInfo) {
  const { t } = useTranslation();

  return (
    <DataSetsPanel title={t('customers.selections.general')} className={styles.fullWidth}>
      <BasicCustomerInfo
        control={control}
        placeholder={placeholder}
        recordId={recordId}
        isUniqueIdentifierEditable={isUniqueIdentifierEditable}
      />
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={VALUE_CHAIN_LIST}
          render={({
            field: { name, value: valueChain, onChange: onChangeValueChain },
            fieldState: { error: errorCurrency },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainPosition')}
              label={t('common.valueChainPosition')}
              options={vcOptionsList}
              value={valueChain?.value ? valueChain : undefined}
              onChange={(value) => {
                const selectedValue = value as IOption;
                onChangeValueChain(selectedValue);
                handleGetVcOptions(selectedValue.value);
                setValue(VALUE_CHAIN_SECTION, undefined as never);
              }}
              error={errorCurrency?.message}
              menuPosition="absolute"
              isDisabled
              required
            />
          )}
        />
        <Controller
          control={control}
          name={VALUE_CHAIN_SECTION}
          render={({
            field: { name, value: valueChainSection, onChange: onChangeValueChainSection },
            fieldState: { error: errorCurrencySection },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainCategory')}
              label={t('common.valueChainCategory')}
              options={vcOptions || listPosition}
              value={valueChainSection?.value ? valueChainSection : null}
              onChange={onChangeValueChainSection}
              error={errorCurrencySection?.message}
              menuPosition="absolute"
              isDisabled={listPosition?.length === 0 && !recordId}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={OWNERSHIP}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('customers.fields.ownership')}
              placeholder={recordId ? placeholder : t('customers.fields.ownership')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.ownership]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={BUSINESS_UNIT_TYPE}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('customers.fields.physicalOrLogical')}
              placeholder={t('customers.fields.physicalOrLogical')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.businessUnitType]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.value?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
      </div>
      <FinancialAndDatesInfo
        control={control}
        placeholder={placeholder}
        dateOpened={dateOpened}
        onChangeDate={onChangeDate}
      />
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={PRIORITY_RATING}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('customers.fields.priorityRating')}
              placeholder={t('customers.fields.priorityRating')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.priorityRating]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.value?.message}
              menuPosition="absolute"
            />
          )}
        />
      </div>
    </DataSetsPanel>
  );
}

export default GeneralCustomerInfo;
